import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import {
  SEED_PLAN_TIERS,
  SEED_FEDERAL_BRACKETS,
  SEED_STANDARD_DEDUCTION,
  SEED_PAYROLL_CONSTANTS,
  SEED_ENGINE_SETTINGS,
  SEED_EMPLOYER_PLAN_RULES,
  SEED_STATE_DATA,
  SEED_REPORT_CONTENT,
} from '../data/seedData';
import { SEED_IBOS_LAYERS } from '../data/ibosDefaults';
import {
  SEED_MIN_WAGE_JURISDICTIONS,
  SEED_LOCAL_INCOME_TAXES,
} from '../data/jurisdictionsSeed';
import {
  SEED_105_QUESTIONS,
  SEED_105_PLAN_TYPES,
  SEED_105_IRS_LIMITS,
  SEED_105_DOCUMENT_TEMPLATES,
  SEED_105_PACKAGES,
  SEED_105_COVERAGE_LAYERS,
} from '../data/section105Defaults';

const STORAGE_KEY = 'hc_s125_config_v8_cache'; // v8: Production config with 51 states + complete reportContent
const OLD_STORAGE_KEYS = ['hc_s125_config_v3', 'hc_s125_config_v2', 'hc_s125_config_v1', 'hc_s125_config_v4', 'hc_s125_config_v4_cache', 'hc_s125_config_v5_cache', 'hc_s125_config_v6_cache', 'hc_s125_config_v7_cache'];
const AUTH_KEY = 'hc_s125_auth';

const ConfigContext = createContext(null);

function serializeConfig(config) {
  const c = JSON.parse(JSON.stringify(config, (key, value) => {
    if (value === Infinity) return '__INFINITY__';
    return value;
  }));
  return c;
}

function deserializeConfig(raw) {
  return JSON.parse(JSON.stringify(raw), (key, value) => {
    if (value === '__INFINITY__') return Infinity;
    return value;
  });
}

// Migrate old plan tiers that used flat fields to new benefits array format
function migratePlanTiers(planTiers) {
  return planTiers.map(tier => {
    if (tier.benefits && tier.benefits.length > 0) return tier;
    // Old format: hospitalDays, hospitalRate, erBenefit, ambulance → convert to benefits array
    const benefits = [
      { name: 'Initial Admission', fee: tier.hospitalRate || 0, quantity: 1, unit: 'day' },
      { name: 'Daily In-Hospital', fee: tier.hospitalRate || 0, quantity: tier.hospitalDays || 1, unit: 'day' },
      { name: 'Emergency Room', fee: tier.erBenefit || 0, quantity: 2, unit: 'visit' },
      { name: 'Ambulance', fee: tier.ambulance || 0, quantity: 2, unit: 'trip' },
      { name: 'Indemnity Claim', fee: tier.monthlyClaim || 0, quantity: 12, unit: 'month' },
    ];
    const { hospitalDays, hospitalRate, erBenefit, ambulance, ...rest } = tier;
    return { ...rest, benefits };
  });
}

// Migrate v3 → v4: Add engine settings and expand state conformity
function migrateToV4(config) {
  // Add engine settings if missing
  if (!config.engineSettings) {
    config.engineSettings = { ...SEED_ENGINE_SETTINGS };
  }
  
  // Ensure all engine settings exist
  const defaults = SEED_ENGINE_SETTINGS;
  Object.keys(defaults).forEach(key => {
    if (config.engineSettings[key] === undefined) {
      config.engineSettings[key] = defaults[key];
    }
  });
  
  // Add employer plan rules if missing
  if (!config.employerPlanRules) {
    config.employerPlanRules = { ...SEED_EMPLOYER_PLAN_RULES };
  }
  
  // Ensure all employer plan rules exist
  const ruleDefaults = SEED_EMPLOYER_PLAN_RULES;
  Object.keys(ruleDefaults).forEach(key => {
    if (config.employerPlanRules[key] === undefined) {
      config.employerPlanRules[key] = ruleDefaults[key];
    }
  });
  
  // Migrate state conformity from boolean to three separate toggles
  Object.keys(config.stateData).forEach(stateCode => {
    const state = config.stateData[stateCode];
    if (state.conformity !== undefined) {
      // Old format: single boolean
      state.conformityIncomeTax = state.conformity;
      state.conformitySdi = true;
      state.conformityPfml = true;
      delete state.conformity;
    }
    // Ensure all three conformity fields exist
    if (state.conformityIncomeTax === undefined) state.conformityIncomeTax = true;
    if (state.conformitySdi === undefined) state.conformitySdi = true;
    if (state.conformityPfml === undefined) state.conformityPfml = true;
  });

  // Phase 17: ensure jurisdiction blocks exist (additive; no destructive merge)
  if (!config.minimumWageJurisdictions) {
    config.minimumWageJurisdictions = JSON.parse(JSON.stringify(SEED_MIN_WAGE_JURISDICTIONS));
  } else {
    if (!config.minimumWageJurisdictions.federal) config.minimumWageJurisdictions.federal = { ...SEED_MIN_WAGE_JURISDICTIONS.federal };
    if (!config.minimumWageJurisdictions.states) config.minimumWageJurisdictions.states = {};
    if (!config.minimumWageJurisdictions.cities) config.minimumWageJurisdictions.cities = {};
  }
  if (!config.localIncomeTaxes) {
    config.localIncomeTaxes = JSON.parse(JSON.stringify(SEED_LOCAL_INCOME_TAXES));
  }

  return config;
}


function loadFromStorage() {
  try {
    // Try current version first
    const stored = localStorage.getItem(STORAGE_KEY);
    console.log('📦 loadFromStorage: Checking for cache key:', STORAGE_KEY, stored ? 'FOUND' : 'NOT FOUND');
    
    if (stored) {
      let config = deserializeConfig(JSON.parse(stored));
      console.log('📦 Loaded config from cache, keys:', Object.keys(config).join(', '));
      console.log('📦 Has planTiers?', config.planTiers?.length);
      console.log('📦 Has reportContent?', !!config.reportContent);
      
      config.planTiers = migratePlanTiers(config.planTiers);
      if (!config.reportContent) config.reportContent = JSON.parse(JSON.stringify(SEED_REPORT_CONTENT));
      
      // Migrate v2 → v3: Add new filing statuses (MFS, QSS)
      if (!config.standardDeduction.mfs) {
        config.standardDeduction.mfs = 15000;
      }
      if (!config.standardDeduction.qss) {
        config.standardDeduction.qss = 30000;
      }
      
      // Migrate v2 → v3: Convert single addlMedicareThreshold to object
      if (config.payrollConstants.addlMedicareThreshold !== undefined && !config.payrollConstants.addlMedicareThresholds) {
        const oldThreshold = config.payrollConstants.addlMedicareThreshold;
        config.payrollConstants.addlMedicareThresholds = {
          single: oldThreshold,
          married: 250000,
          mfs: 125000,
          hoh: oldThreshold,
          qss: 200000
        };
        delete config.payrollConstants.addlMedicareThreshold;
      }
      
      // Ensure addlMedicareThresholds has all 5 filing statuses
      if (config.payrollConstants.addlMedicareThresholds) {
        const thresholds = config.payrollConstants.addlMedicareThresholds;
        if (!thresholds.single) thresholds.single = 200000;
        if (!thresholds.married) thresholds.married = 250000;
        if (!thresholds.mfs) thresholds.mfs = 125000;
        if (!thresholds.hoh) thresholds.hoh = 200000;
        if (!thresholds.qss) thresholds.qss = 200000;
      }
      
      // Migrate v3 → v4
      config = migrateToV4(config);
      
      return config;
    }
    // Migrate from older versions if present
    for (const oldKey of OLD_STORAGE_KEYS) {
      const oldStored = localStorage.getItem(oldKey);
      if (oldStored) {
        let config = deserializeConfig(JSON.parse(oldStored));
        config.planTiers = migratePlanTiers(config.planTiers);
        if (!config.reportContent) config.reportContent = JSON.parse(JSON.stringify(SEED_REPORT_CONTENT));
        
        // Apply same v2→v3 migrations
        if (!config.standardDeduction.mfs) {
          config.standardDeduction.mfs = 15000;
        }
        if (!config.standardDeduction.qss) {
          config.standardDeduction.qss = 30000;
        }
        
        if (config.payrollConstants.addlMedicareThreshold !== undefined && !config.payrollConstants.addlMedicareThresholds) {
          const oldThreshold = config.payrollConstants.addlMedicareThreshold;
          config.payrollConstants.addlMedicareThresholds = {
            single: oldThreshold,
            married: 250000,
            mfs: 125000,
            hoh: oldThreshold,
            qss: 200000
          };
          delete config.payrollConstants.addlMedicareThreshold;
        }
        
        if (config.payrollConstants.addlMedicareThresholds) {
          const thresholds = config.payrollConstants.addlMedicareThresholds;
          if (!thresholds.single) thresholds.single = 200000;
          if (!thresholds.married) thresholds.married = 250000;
          if (!thresholds.mfs) thresholds.mfs = 125000;
          if (!thresholds.hoh) thresholds.hoh = 200000;
          if (!thresholds.qss) thresholds.qss = 200000;
        }
        
        // Apply v3 → v4 migration
        config = migrateToV4(config);
        
        localStorage.removeItem(oldKey);
        return config;
      }
    }
  } catch (e) {
    console.warn('Failed to load config from localStorage:', e);
  }
  return null;
}

function saveToStorage(config) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializeConfig(config)));
  } catch (e) {
    console.warn('Failed to save config to localStorage:', e);
  }
}

function getDefaultConfig() {
  return {
    planTiers: JSON.parse(JSON.stringify(SEED_PLAN_TIERS)),
    federalBrackets: JSON.parse(JSON.stringify(SEED_FEDERAL_BRACKETS, (k, v) => v === Infinity ? '__INFINITY__' : v), (k, v) => v === '__INFINITY__' ? Infinity : v),
    standardDeduction: { ...SEED_STANDARD_DEDUCTION },
    payrollConstants: { ...SEED_PAYROLL_CONSTANTS },
    engineSettings: { ...SEED_ENGINE_SETTINGS },
    employerPlanRules: { ...SEED_EMPLOYER_PLAN_RULES },
    stateData: JSON.parse(JSON.stringify(SEED_STATE_DATA, (k, v) => v === Infinity ? '__INFINITY__' : v), (k, v) => v === '__INFINITY__' ? Infinity : v),
    minimumWageJurisdictions: JSON.parse(JSON.stringify(SEED_MIN_WAGE_JURISDICTIONS)),
    localIncomeTaxes: JSON.parse(JSON.stringify(SEED_LOCAL_INCOME_TAXES, (k, v) => v === Infinity ? '__INFINITY__' : v), (k, v) => v === '__INFINITY__' ? Infinity : v),
    reportContent: JSON.parse(JSON.stringify(SEED_REPORT_CONTENT)),
    revenueSettings: {
      spreadAllocation: [
        { category: 'Broker Commission', percentage: 15 },
        { category: 'Vendor Platform', percentage: 25 },
        { category: 'Hospitalization Underwriting', percentage: 30 },
        { category: 'HealthCues Administration', percentage: 30 },
      ],
      showAllocationInReports: true,
      showAllocationInEmployerPDF: false,
      benefitValues: [
        { benefit: 'Telemedicine (24/7, $0 copay)', value: 75 },
        { benefit: 'Prescription Program (70+ meds)', value: 30 },
        { benefit: 'Health Coaching & Goals', value: 25 },
        { benefit: 'DNA & Biometric Screening', value: 15 },
        { benefit: 'Behavioral Health / Teletherapy', value: 40 },
        { benefit: 'Personal Training & Nutrition', value: 15 },
      ],
      showBenefitValues: true,
    },
    advisorSettings: {
      hoursScenarios: [20, 25, 30],
      waitingPeriodScenarios: [0, 30, 60, 90],
      nearEligibleSalaryBuffer: 3000,
      hceWarningThreshold: 20,
      highImpactEmployeeThreshold: 5,
      highImpactSavingsThreshold: 5000,
      mediumImpactEmployeeThreshold: 2,
      mediumImpactSavingsThreshold: 2000,
      showInPDF: true,
      showNonDiscrimination: true,
    },
    emailTemplates: {
      admin: { subject: '', greeting: '', body: '', bullets: [], ctaLabel: '', footer: '' },
      client: { subject: '', greeting: '', body: '', bullets: [], ctaLabel: '', footer: '' },
      employee: { subject: '', greeting: '', body: '', bullets: [], ctaLabel: '', footer: '' }
    },
    ibosLayers: JSON.parse(JSON.stringify(SEED_IBOS_LAYERS)),
    autoMessageTemplates: {
      contract_signed: {
        enabled: true,
        template: "✓ {contactName} signed {contractName}. That's one step closer to go-live!",
      },
      banking_verified: {
        enabled: true,
        template: "✓ Banking verified ({bankName} {accountMasked}). You're all set for payroll integration.",
      },
      census_uploaded: {
        enabled: true,
        template: "✓ Census uploaded with {count} employees (avg salary {avgSalary}). Your full workforce analysis is ready to review.",
      },
      enrollment_milestone: {
        enabled: true,
        template: "🎉 {count} employees have enrolled! Your program is off to a strong start.",
      },
    },
    section105Config: {
      questions: JSON.parse(JSON.stringify(SEED_105_QUESTIONS)),
      planTypes: JSON.parse(JSON.stringify(SEED_105_PLAN_TYPES)),
      irsLimits: JSON.parse(JSON.stringify(SEED_105_IRS_LIMITS)),
      documentTemplates: JSON.parse(JSON.stringify(SEED_105_DOCUMENT_TEMPLATES)),
      packages: JSON.parse(JSON.stringify(SEED_105_PACKAGES)),
      coverageLayers: JSON.parse(JSON.stringify(SEED_105_COVERAGE_LAYERS)),
    },
  };
}

// Ensure engineSettings exists with all required fields
function ensureEngineSettings(config) {
  if (!config.engineSettings) {
    config.engineSettings = { ...SEED_ENGINE_SETTINGS };
  } else {
    // Ensure all fields exist
    const defaults = SEED_ENGINE_SETTINGS;
    Object.keys(defaults).forEach(key => {
      if (config.engineSettings[key] === undefined) {
        config.engineSettings[key] = defaults[key];
      }
    });
  }
  return config;
}

// Ensure all business logic config sections exist
function ensureBusinessLogicDefaults(config) {
  // Ensure revenueSettings
  if (!config.revenueSettings) {
    const defaults = getDefaultConfig();
    config.revenueSettings = defaults.revenueSettings;
  } else {
    // Ensure key properties exist
    if (!config.revenueSettings.spreadAllocation) {
      config.revenueSettings.spreadAllocation = [
        { category: 'Broker Commission', percentage: 15 },
        { category: 'Vendor Platform', percentage: 25 },
        { category: 'Hospitalization Underwriting', percentage: 30 },
        { category: 'HealthCues Margin', percentage: 30 },
      ];
    }
    if (!config.revenueSettings.benefitValues) {
      config.revenueSettings.benefitValues = [
        { benefit: 'Telemedicine (24/7, $0 copay)', value: 75 },
        { benefit: 'Prescription Program (70+ meds)', value: 30 },
        { benefit: 'Health Coaching & Goals', value: 25 },
        { benefit: 'DNA & Biometric Screening', value: 15 },
        { benefit: 'Behavioral Health / Teletherapy', value: 40 },
        { benefit: 'Personal Training & Nutrition', value: 15 },
      ];
    }
    if (config.revenueSettings.showAllocationInReports === undefined) {
      config.revenueSettings.showAllocationInReports = true;
    }
    if (config.revenueSettings.showAllocationInEmployerPDF === undefined) {
      config.revenueSettings.showAllocationInEmployerPDF = false;
    }
    if (config.revenueSettings.showBenefitValues === undefined) {
      config.revenueSettings.showBenefitValues = true;
    }
  }

  // Ensure advisorSettings
  if (!config.advisorSettings) {
    const defaults = getDefaultConfig();
    config.advisorSettings = defaults.advisorSettings;
  } else {
    // Ensure key properties exist
    if (!config.advisorSettings.hoursScenarios) config.advisorSettings.hoursScenarios = [20, 25, 30];
    if (!config.advisorSettings.waitingPeriodScenarios) config.advisorSettings.waitingPeriodScenarios = [0, 30, 60, 90];
    if (config.advisorSettings.nearEligibleSalaryBuffer === undefined) config.advisorSettings.nearEligibleSalaryBuffer = 3000;
    if (config.advisorSettings.hceWarningThreshold === undefined) config.advisorSettings.hceWarningThreshold = 20;
    if (config.advisorSettings.highImpactEmployeeThreshold === undefined) config.advisorSettings.highImpactEmployeeThreshold = 5;
    if (config.advisorSettings.highImpactSavingsThreshold === undefined) config.advisorSettings.highImpactSavingsThreshold = 5000;
    if (config.advisorSettings.mediumImpactEmployeeThreshold === undefined) config.advisorSettings.mediumImpactEmployeeThreshold = 2;
    if (config.advisorSettings.mediumImpactSavingsThreshold === undefined) config.advisorSettings.mediumImpactSavingsThreshold = 2000;
    if (config.advisorSettings.showInPDF === undefined) config.advisorSettings.showInPDF = true;
    if (config.advisorSettings.showNonDiscrimination === undefined) config.advisorSettings.showNonDiscrimination = true;
  }

  // Ensure employerPlanRules (may already exist from migration)
  if (!config.employerPlanRules) {
    config.employerPlanRules = { ...SEED_EMPLOYER_PLAN_RULES };
  } else {
    const ruleDefaults = SEED_EMPLOYER_PLAN_RULES;
    Object.keys(ruleDefaults).forEach(key => {
      if (config.employerPlanRules[key] === undefined) {
        config.employerPlanRules[key] = ruleDefaults[key];
      }
    });
  }

  // Ensure ibosLayers exists with the 8 default layers
  if (!config.ibosLayers || !Array.isArray(config.ibosLayers) || config.ibosLayers.length === 0) {
    config.ibosLayers = JSON.parse(JSON.stringify(SEED_IBOS_LAYERS));
  } else {
    // Merge any missing seed layers (preserves user customizations but adds new seed layers if added later)
    const existingIds = new Set(config.ibosLayers.map(l => l.id));
    SEED_IBOS_LAYERS.forEach(seedLayer => {
      if (!existingIds.has(seedLayer.id)) {
        config.ibosLayers.push(JSON.parse(JSON.stringify(seedLayer)));
      }
    });
    // Sort by order to keep consistent display
    config.ibosLayers.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  // Ensure autoMessageTemplates default is present for any missing event keys
  const DEFAULT_AUTO_MSGS = {
    contract_signed: {
      enabled: true,
      template: "✓ {contactName} signed {contractName}. That's one step closer to go-live!",
    },
    banking_verified: {
      enabled: true,
      template: "✓ Banking verified ({bankName} {accountMasked}). You're all set for payroll integration.",
    },
    census_uploaded: {
      enabled: true,
      template: "✓ Census uploaded with {count} employees (avg salary {avgSalary}). Your full workforce analysis is ready to review.",
    },
    enrollment_milestone: {
      enabled: true,
      template: "🎉 {count} employees have enrolled! Your program is off to a strong start.",
    },
  };
  if (!config.autoMessageTemplates || typeof config.autoMessageTemplates !== 'object') {
    config.autoMessageTemplates = DEFAULT_AUTO_MSGS;
  } else {
    Object.keys(DEFAULT_AUTO_MSGS).forEach(key => {
      if (!config.autoMessageTemplates[key]) {
        config.autoMessageTemplates[key] = DEFAULT_AUTO_MSGS[key];
      }
    });
  }

  // Ensure section105Config (Plan Advisor) exists with all defaults
  if (!config.section105Config || typeof config.section105Config !== 'object') {
    config.section105Config = {
      questions: JSON.parse(JSON.stringify(SEED_105_QUESTIONS)),
      planTypes: JSON.parse(JSON.stringify(SEED_105_PLAN_TYPES)),
      irsLimits: JSON.parse(JSON.stringify(SEED_105_IRS_LIMITS)),
      documentTemplates: JSON.parse(JSON.stringify(SEED_105_DOCUMENT_TEMPLATES)),
      packages: JSON.parse(JSON.stringify(SEED_105_PACKAGES)),
      coverageLayers: JSON.parse(JSON.stringify(SEED_105_COVERAGE_LAYERS)),
    };
  } else {
    const s = config.section105Config;
    if (!Array.isArray(s.questions) || s.questions.length === 0) {
      s.questions = JSON.parse(JSON.stringify(SEED_105_QUESTIONS));
    }
    if (!Array.isArray(s.planTypes) || s.planTypes.length === 0) {
      s.planTypes = JSON.parse(JSON.stringify(SEED_105_PLAN_TYPES));
    } else {
      const existingIds = new Set(s.planTypes.map(p => p.id));
      SEED_105_PLAN_TYPES.forEach(seed => {
        if (!existingIds.has(seed.id)) {
          s.planTypes.push(JSON.parse(JSON.stringify(seed)));
        }
      });
    }
    if (!s.irsLimits || typeof s.irsLimits !== 'object') {
      s.irsLimits = JSON.parse(JSON.stringify(SEED_105_IRS_LIMITS));
    }
    if (!s.documentTemplates || typeof s.documentTemplates !== 'object') {
      s.documentTemplates = JSON.parse(JSON.stringify(SEED_105_DOCUMENT_TEMPLATES));
    } else {
      // Merge any missing template keys
      Object.entries(SEED_105_DOCUMENT_TEMPLATES).forEach(([k, v]) => {
        if (!s.documentTemplates[k]) s.documentTemplates[k] = v;
      });
    }
    // Packages
    if (!Array.isArray(s.packages) || s.packages.length === 0) {
      s.packages = JSON.parse(JSON.stringify(SEED_105_PACKAGES));
    } else {
      const existingPkgIds = new Set(s.packages.map(p => p.id));
      SEED_105_PACKAGES.forEach(seed => {
        if (!existingPkgIds.has(seed.id)) {
          s.packages.push(JSON.parse(JSON.stringify(seed)));
        }
      });
    }
    // Coverage layers
    if (!s.coverageLayers || typeof s.coverageLayers !== 'object') {
      s.coverageLayers = JSON.parse(JSON.stringify(SEED_105_COVERAGE_LAYERS));
    } else {
      Object.entries(SEED_105_COVERAGE_LAYERS).forEach(([k, v]) => {
        if (!s.coverageLayers[k]) s.coverageLayers[k] = JSON.parse(JSON.stringify(v));
      });
    }
  }

  return config;
}

export function ConfigProvider({ children }) {
  // Initialize with cached config if available, otherwise use defaults
  const [config, setConfig] = useState(() => {
    try {
      const cached = loadFromStorage();
      if (cached) {
        return ensureEngineSettings(cached);
      }
    } catch (e) {
      console.warn('Failed to load cached config:', e);
    }
    return getDefaultConfig();
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return localStorage.getItem(AUTH_KEY) === 'true';
    } catch {
      return false;
    }
  });
  
  // Load config from API on mount (if authenticated)
  useEffect(() => {
    const loadConfigFromAPI = async () => {
      try {
        const token = localStorage.getItem('healthcues_token');
        if (!token) {
          console.log('⚠️ No token found, using cached/default config');
          // Not authenticated, use defaults/cache
          setLoading(false);
          return;
        }
        
        console.log('🌐 Fetching config from API...');
        const apiConfig = await api.config.get();
        
        console.log('✅ API returned config, keys:', Object.keys(apiConfig).join(', '));
        console.log('   API planTiers:', apiConfig.planTiers?.length);
        console.log('   API reportContent:', apiConfig.reportContent ? 'exists' : 'MISSING');
        console.log('   API emailTemplates:', apiConfig.emailTemplates ? 'exists' : 'MISSING');
        
        // Convert API format to internal format (handle Infinity)
        const processedConfig = deserializeConfig(apiConfig);
        const finalConfig = ensureBusinessLogicDefaults(ensureEngineSettings(processedConfig));
        
        console.log('📝 Setting config state with API data');
        setConfig(finalConfig);
        
        // Cache for faster subsequent loads (but always fetch fresh from API)
        saveToStorage(processedConfig);
        console.log('💾 Saved to localStorage cache');
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Failed to load config from API:', error);
        // Keep using cached/default config
        setLoading(false);
      }
    };
    
    loadConfigFromAPI();
  }, []);

  // Save config to API (debounced to avoid too many calls)
  const saveConfigToAPI = useCallback(async (configUpdates) => {
    try {
      setSaving(true);
      await api.config.update(configUpdates);
      
      // NO LONGER SAVING TO LOCALSTORAGE - MongoDB is single source of truth
      
      setSaving(false);
      return true;
    } catch (error) {
      console.error('Failed to save config to API:', error);
      setSaving(false);
      return false;
    }
  }, []);

  // REMOVED: No longer caching config changes to localStorage

  const updatePlanTiers = useCallback((planTiers) => {
    setConfig(prev => ({ ...prev, planTiers }));
  }, []);

  const updateFederalBrackets = useCallback((federalBrackets) => {
    setConfig(prev => ({ ...prev, federalBrackets }));
  }, []);

  const updateStandardDeduction = useCallback((standardDeduction) => {
    setConfig(prev => ({ ...prev, standardDeduction }));
  }, []);

  const updatePayrollConstants = useCallback((payrollConstants) => {
    setConfig(prev => ({ ...prev, payrollConstants }));
  }, []);

  const updateStateData = useCallback((stateData) => {
    setConfig(prev => ({ ...prev, stateData }));
  }, []);

  const updateSingleState = useCallback((stateCode, stateInfo) => {
    setConfig(prev => ({
      ...prev,
      stateData: { ...prev.stateData, [stateCode]: stateInfo },
    }));
  }, []);

  const updateReportContent = useCallback((reportContent) => {
    setConfig(prev => ({ ...prev, reportContent }));
  }, []);

  const updateEngineSettings = useCallback((engineSettings) => {
    setConfig(prev => ({ ...prev, engineSettings }));
  }, []);

  const updateEmployerPlanRules = useCallback((employerPlanRules) => {
    setConfig(prev => ({ ...prev, employerPlanRules }));
  }, []);

  const updateEmailTemplates = useCallback((emailTemplates) => {
    setConfig(prev => ({ ...prev, emailTemplates }));
  }, []);

  const updateConfig = useCallback(async (configUpdates) => {
    // Update local state
    setConfig(prev => ({ ...prev, ...configUpdates }));
    
    // Save to API
    return await saveConfigToAPI(configUpdates);
  }, [saveConfigToAPI]);


  const resetToDefaults = useCallback(() => {
    const defaults = getDefaultConfig();
    setConfig(defaults);
  }, []);

  const login = useCallback((email, password) => {
    if (email === 'nigel.ingham@kindwellone.com' && password === '@Laguna26') {
      setIsAuthenticated(true);
      try { localStorage.setItem(AUTH_KEY, 'true'); } catch {}
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    try { localStorage.removeItem(AUTH_KEY); } catch {}
  }, []);

  return (
    <ConfigContext.Provider value={{
      config,
      loading,
      saving,
      updatePlanTiers,
      updateFederalBrackets,
      updateStandardDeduction,
      updatePayrollConstants,
      updateEngineSettings,
      updateEmployerPlanRules,
      updateEmailTemplates,
      updateConfig,
      updateStateData,
      updateSingleState,
      updateReportContent,
      resetToDefaults,
      isAuthenticated,
      login,
      logout,
      saveConfigToAPI,
    }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
}
